import {Component} from 'panel';

import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {SelectDropdown} from 'common/components/select';
import {KEY_VALUES} from 'common/util/dom';

import '../tag';
import css from './index.styl?inline';
import template from './index.jade';

const MIN_INPUT_WIDTH = `12px`;

export class TagInput extends Component {
  static get attrsSchema() {
    return {
      ...super.attrsSchema,
      error: `boolean`,
      'min-tags': {type: `number`, default: 1},
      'search-text': {type: `string`, default: `Search`},
      'show-search-icon': `boolean`,
      tags: {
        type: `json`,
        default: [],
        description: `interface: TagItem[], with a label and optional isDisabled
        and attrs params (attrs gets passed down to the mp-tag attrs)`,
      },
      placeholder: `string`,
      'disable-input': `boolean`,
      'mp-select-attrs': `json`,
    };
  }

  get config() {
    return {
      defaultState: {
        isInputFocused: false,
        inputText: ``,
        inputWidth: MIN_INPUT_WIDTH,
        tags: new Set(),
      },
      css,
      template,
      helpers: {
        processMpSelectAttrs: () => {
          const mpSelectAttrs = this.attr(`mp-select-attrs`);
          const mpSelectAttributeSchema = SelectDropdown.attrsSchema;
          const jsonAttrsNames = Object.entries(mpSelectAttributeSchema)
            .filter(([, value]) => value?.type === `json` || value[1] === `json`)
            .map(([key]) => key);

          const processedAttrs = {
            elref: `mpTagInputMpSelect`,
          };
          for (const key in mpSelectAttrs) {
            processedAttrs[key] = jsonAttrsNames.includes(key)
              ? JSON.stringify(mpSelectAttrs[key])
              : mpSelectAttrs[key];
          }
          return processedAttrs;
        },
        onSelectMenuChange: ({detail}) => {
          this.update({isInputFocused: detail.isOpen});
        },
        onSelectChange: (ev) => {
          this.dispatchEvent(new CustomEvent(`optionSelected`, {detail: ev.detail}));
        },
        onFocusChange: (ev) => {
          this.update({isInputFocused: ev.type === `focus`});
        },
        canRemoveTag: (tag) => this.state.tags.size > this.attr(`min-tags`) && !tag.isDisabled,
        setInputText: (ev) => {
          this.update({inputText: ev.target.value});
          this._resizeInput();
          this.dispatchEvent(new CustomEvent(`change`, {detail: {value: this.state.inputText, action: `text`}}));
        },
        removeTag: (tagName) => () => this._removeTag(tagName),
        focusInput: () => this.focus(),
        handleKeyDown: (ev) => {
          // ev.keyCode is deprecated
          if (ev.code === KEY_VALUES.BACKSPACE && this.state.inputText === ``) {
            const tagList = Array.from(this.state.tags);
            for (let i = tagList.length - 1; i >= 0; i--) {
              if (this.config.helpers.canRemoveTag(tagList[i])) {
                this._removeTag(tagList[i]);
                break;
              }
            }
          }
        },
        getPlaceholder: () => {
          if (this.state.tags.size) {
            return ``;
          } else if (this.state.showSearchIcon && this.attr(`search-text`)) {
            return this.attr(`search-text`);
          }
          return this.attr(`placeholder`) || ``;
        },
      },
      useShadowDom: true,
    };
  }

  attributeChangedCallback(attr) {
    super.attributeChangedCallback(...arguments);
    if (attr === `tags`) {
      this.update({tags: new Set(this.attr(`tags`)), inputText: ``});
    }
  }

  focus() {
    const searchInput = this.el.querySelector(`.mp-tag-input-search-input`);
    if (searchInput) {
      searchInput.focus();
    }
  }

  _resizeInput() {
    // make the search input width dynamic
    const span = document.createElement(`span`);
    span.className = `mp-tag-input-search-input-dummy`;
    span.innerText = this.el.querySelector(`.mp-tag-input-search-input`).value;
    this.el.appendChild(span);
    const buffer = 24;
    this.update({inputWidth: span.offsetWidth + buffer + `px`});
    this.el.removeChild(span);
  }

  _removeTag(tag) {
    const tags = this.state.tags;
    tags.delete(tag);
    this.update({tags});
    this.dispatchEvent(new CustomEvent(`change`, {detail: {tag, action: `removeTag`}}));
    requestAnimationFrame(() => this.focus());
  }

  _inputText(field, defaultText) {
    const inputText = this.attr(`input-text`);
    return inputText && inputText[field] ? inputText[field] : defaultText;
  }
}

export const TagInputEl = defineAndMakeJsxFactory(`mp-tag-input`, TagInput);
